import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { Collection } from '@sorare/core/src/atoms/icons/Collection';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, BrandS } from '@sorare/core/src/atoms/typography';
import { Card } from '@sorare/core/src/components/card/Card';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { ANY_SPORT_SERIES_MARKET_COMMON_PACKS } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import type {
  TaskCardsMyRelatedCardsQuery,
  TaskCardsMyRelatedCardsQueryVariables,
  TaskCards_taskInterface,
} from './__generated__/index.graphql';

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: var(--unit);
  width: 100%;
`;

const CardSlot = styled(ButtonBase)`
  aspect-ratio: var(--card-aspect-ratio);
  min-width: 0;
`;

const RemainingSlot = styled(CardSlot)`
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--c-nd-150);
  border: 1px dashed var(--c-nd-400);
  border-radius: var(--unit);
`;

const TASK_CARDS_MY_RELATED_CARDS_QUERY = gql`
  query TaskCardsMyRelatedCardsQuery(
    $taskId: ID!
    $after: String
    $pageSize: Int
  ) {
    currentUser {
      slug
      task(id: $taskId) {
        id
        ... on CardsCountTask {
          id
          myRelatedCards(after: $after, first: $pageSize) {
            nodes {
              slug
              ...Card_anyCard
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      }
    }
  }
  ${Card.fragments.anyCard}
` as TypedDocumentNode<
  TaskCardsMyRelatedCardsQuery,
  TaskCardsMyRelatedCardsQueryVariables
>;

type Props = {
  task: TaskCards_taskInterface;
};

const PAGE_SIZE = 10;

export const TaskCards = ({ task }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const openDialog = useOpenDialog();
  const isCardsCountTask = isType(task, 'CardsCountTask');
  const { data, loading, loadMore } = usePaginatedQuery(
    TASK_CARDS_MY_RELATED_CARDS_QUERY,
    {
      variables: { taskId: idFromObject(task.id), pageSize: PAGE_SIZE },
      connection: 'AnyCardInterfaceConnection',
      skip: !isCardsCountTask,
    }
  );

  if (!isCardsCountTask) {
    return null;
  }

  const paginatedTask = data?.currentUser?.task;
  const myRelatedCards = isType(paginatedTask, 'CardsCountTask')
    ? paginatedTask.myRelatedCards
    : null;

  const { scoreToReach } = task;
  const cardList = myRelatedCards?.nodes || [];
  const visibleCards = cardList;
  const hasMoreRelatedCards = myRelatedCards?.pageInfo?.hasNextPage;
  const remainingToDisplayInGrid = Math.max(
    0,
    myRelatedCards?.pageInfo.hasNextPage ? 0 : scoreToReach - cardList.length
  );

  return (
    <Vertical gap={2}>
      <Horizontal>
        <div style={{ color: 'var(--c-set-main-color)' }}>
          <Collection size={2} />
        </div>
        <BrandS>
          <FormattedMessage defaultMessage="My collection" />
        </BrandS>
      </Horizontal>
      <CardsGrid>
        {visibleCards.map(card => (
          <CardSlot
            key={card.slug}
            onClick={() => openDialog(DialogKey.card, card.slug)}
          >
            <Card card={card} width={160} />
          </CardSlot>
        ))}
        {Array.from({ length: remainingToDisplayInGrid }, (_, i) => (
          <RemainingSlot
            key={`remaining-${i}`}
            to={generateSportContextPath(ANY_SPORT_SERIES_MARKET_COMMON_PACKS)}
          >
            <BrandM bold color="var(--c-nd-500)">
              ?
            </BrandM>
          </RemainingSlot>
        ))}
      </CardsGrid>
      {!!hasMoreRelatedCards && (
        <LoadingButton
          size="medium"
          color="quaternary"
          disabled={loading}
          onClick={async () => {
            await loadMore(false, {
              after: myRelatedCards?.pageInfo?.endCursor,
            });
          }}
        >
          <FormattedMessage {...glossary.viewMore} />
        </LoadingButton>
      )}
    </Vertical>
  );
};

TaskCards.fragments = {
  taskInterface: gql`
    fragment TaskCards_taskInterface on TaskInterface {
      id
      ... on CardsCountTask {
        id
        scoreToReach
      }
    }
  ` as TypedDocumentNode<TaskCards_taskInterface>,
};
